import {IUserProfile, IUserStats} from '@/interfaces/user';
import {AppNotification, MainState, UploadProgressState} from './state';
import {getStoreAccessors} from 'typesafe-vuex';
import {State} from '../state';
import {IForm, IFormBatch, IFormBatchStats, IFormValues} from '@/interfaces/form';
import {IExternalData} from '@/interfaces/external-data';
import {ISearchEngine} from '@/interfaces/search_engine';
import {IFileUpload} from '@/interfaces/file';
import {IQueryBuilder} from '@/interfaces/query-builder';
import {IPrinterLog} from '@/interfaces/printer-logs';
import {IAccessLog} from '@/interfaces/access-log';
import {IVisitedPages} from '@/interfaces/visited-pages';
import {IPermission} from '@/interfaces/permission';


export const mutations = {
    setToken(state: MainState, payload: string) {
        state.token = payload;
    },
    setLoggedIn(state: MainState, payload: boolean) {
        state.isLoggedIn = payload;
    },
    setLogInError(state: MainState, payload: boolean) {
        state.logInError = payload;
    },
    setNoServerConnection(state: MainState, payload: boolean) {
        state.noServerConnection = payload;
    },
    setUserProfile(state: MainState, payload: IUserProfile) {
        state.userProfile = payload;
    },
    setUserProfileStats(state: MainState, payload: IUserStats) {
        state.userStats = payload;
    },
    setDashboardMiniDrawer(state: MainState, payload: boolean) {
        state.dashboardMiniDrawer = payload;
    },
    setDashboardShowDrawer(state: MainState, payload: boolean) {
        state.dashboardShowDrawer = payload;
    },
    addNotification(state: MainState, payload: AppNotification) {
        state.notifications.push(payload);
    },
    removeNotification(state: MainState, payload: AppNotification) {
        state.notifications = state.notifications.filter((notification) => notification !== payload);
    },
    setUploadProgress(state: MainState, payload: Partial<UploadProgressState>) {
        state.uploadProgress = {...state.uploadProgress, ...payload};
    },
    cancelUploadProgress(state: MainState) {
        state.uploadProgress = {
            ...state.uploadProgress,
            cancelled: true,
            canContinue: true,
            message: 'Upload cancelado pelo usuário.',
        };
    },
    clearUploadProgress(state: MainState) {
        state.uploadProgress = {
            active: false,
            current: 0,
            total: 0,
            attempt: 0,
            maxAttempts: 5,
            filename: '',
            message: '',
            canContinue: false,
            cancelled: false,
        };
    },
    setForm(state: MainState, payload: IForm) {
        const filtered = state.forms.filter((item: IForm) => item.id !== payload.id);
        filtered.push(payload);
        state.forms = filtered;
    },
    setForms(state: MainState, payload: IForm[]) {
        state.forms = payload;
    },
    setMenuForm(state: MainState, payload: IForm) {
        const filtered = state.menu_forms.filter((item: IForm) => item.id !== payload.id);
        filtered.push(payload);
        state.menu_forms = filtered;
    },
    setMenuForms(state: MainState, payload: IForm[]) {
        state.menu_forms = payload;
    },
    setFormValues(state: MainState, payload: IFormValues) {
        state.form_values = payload;
        const filtered = state.forms_values.filter((item: IFormValues) => item.id !== payload.id);
        filtered.push(payload);
        state.forms_values = filtered;
    },
    setFormsValues(state: MainState, payload: IFormValues[]) {
        state.forms_values = payload;
    },
    setCurrentFormValues(state: MainState, payload: IFormValues) {
        state.form_values = payload;
    },
    setFormsFiles(state: MainState, payload: IFileUpload) {
        const filtered = state.forms_files?.filter((item: IFileUpload) => item.id !== payload.id);
        filtered.push(payload);
        state.forms_files = filtered;
    },

    setExternalData(state: MainState, payload: IExternalData) {
        state.external_data = payload;
    },

    setSearchEngine(state: MainState, payload: ISearchEngine) {
        state.search_engine = payload;
    },
    setFormBatches(state: MainState, payload: IFormBatchStats) {
        state.form_batches = payload;
    },
    setFormBatch(state: MainState, payload: IFormBatch) {
        // @ts-ignore
        const filtered = state.form_batches.items.filter((item: IFormBatch) => item.id !== payload.id);
        // @ts-ignore
        filtered.push(payload);
        // @ts-ignore
        state.form_batches.items = filtered;
    },
    setQueryBuilder(state: MainState, payload: IQueryBuilder | null) {
        state.query_builder = payload;
    },

    setPrinterLog(state: MainState, payload: IPrinterLog) {
        state.printer_log = payload;
        const filtered = state.printer_logs.filter((item: IPrinterLog) => item.id !== payload.id);
        filtered.push(payload);
        state.printer_logs = filtered;
    },

    setPrinterLogs(state: MainState, payload: IPrinterLog[]) {
        state.printer_logs = payload;
    },

    setAccessLog(state: MainState, payload: IAccessLog) {
        state.access_log = payload;
        const filtered = state.access_logs.filter((item: IAccessLog) => item.id !== payload.id);
        filtered.push(payload);
        state.access_logs = filtered;
    },
    setAccessLogs(state: MainState, payload: IAccessLog[]) {
        state.access_logs = payload;
    },
    setVisitedPage(state: MainState, payload: IVisitedPages) {
        const latestPages = state.visited_pages;
        const hasPage = latestPages.find((item: IVisitedPages) => item.page_url === payload.page_url);
        if (hasPage) {
            return;
        }
        if (latestPages.length > 5) {
            latestPages.shift();
        }
        latestPages.push(payload);
        localStorage.setItem('latest_visited_pages', JSON.stringify(latestPages));
    },
    setPermissions(state: MainState, payload: IPermission[]) {
        state.permissions = payload;
    },
};

const {commit} = getStoreAccessors<MainState | any, State>('');

export const commitSetDashboardMiniDrawer = commit(mutations.setDashboardMiniDrawer);
export const commitSetDashboardShowDrawer = commit(mutations.setDashboardShowDrawer);
export const commitSetLoggedIn = commit(mutations.setLoggedIn);
export const commitSetLogInError = commit(mutations.setLogInError);
export const commitSetNoServerConnection = commit(mutations.setNoServerConnection);
export const commitSetToken = commit(mutations.setToken);
export const commitSetUserProfile = commit(mutations.setUserProfile);
export const commitSetUserProfileStats = commit(mutations.setUserProfileStats);
export const commitAddNotification = commit(mutations.addNotification);
export const commitRemoveNotification = commit(mutations.removeNotification);
export const commitSetUploadProgress = commit(mutations.setUploadProgress);
export const commitCancelUploadProgress = commit(mutations.cancelUploadProgress);
export const commitClearUploadProgress = commit(mutations.clearUploadProgress);

export const commitSetForm = commit(mutations.setForm);
export const commitSetForms = commit(mutations.setForms);
export const commitSetMenuForm = commit(mutations.setMenuForm);
export const commitSetMenuForms = commit(mutations.setMenuForms);
export const commitSetFormsFiles = commit(mutations.setFormsFiles);

export const commitSetFormValues = commit(mutations.setFormValues);
export const commitSetCurrentFormValues = commit(mutations.setCurrentFormValues);
export const commitSetFormsValues = commit(mutations.setFormsValues);

export const commitSetExternalData = commit(mutations.setExternalData);

export const commitSetSearchEngine = commit(mutations.setSearchEngine);

export const commitSetFormBatch = commit(mutations.setFormBatch);
export const commitSetFormBatches = commit(mutations.setFormBatches);

export const commitSetQueryBuilder = commit(mutations.setQueryBuilder);

export const commitSetPrinterLog = commit(mutations.setPrinterLog);
export const commitSetPrinterLogs = commit(mutations.setPrinterLogs);

export const commitSetAccessLog = commit(mutations.setAccessLog);
export const commitSetAccessLogs = commit(mutations.setAccessLogs);

export const commitSetVisitedPage = commit(mutations.setVisitedPage);

export const commitSetPermissions = commit(mutations.setPermissions);
